/**
 * CF-FEEDBACK-V1 — player feedback form.
 *
 * Scorer scope-sign 2026-04-26T14:30Z:
 *  - 1-5 star rating (interactive)
 *  - Optional textarea (max 500 chars + counter)
 *  - Distinct loading / success / error states (no silent catches)
 *  - r.ok guard before json (CF-77 discipline)
 *  - Explicit "do not share seed phrases or private keys" copy
 *  - Reachable from Activity footer; not in main bottom nav
 */

import { useState } from 'react';
import { apiFetch } from '../lib/api';
import { useT } from '../i18n';

interface FeedbackProps {
    apiBase: string;
    walletAddress?: string;
    onClose: () => void;
}

const APP_VERSION = 'CF-FEEDBACK-V1';

export function Feedback({ apiBase, walletAddress, onClose }: FeedbackProps) {
    const t = useT();
    const [stars, setStars] = useState<number>(0);
    const [hoverStars, setHoverStars] = useState<number>(0);
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const charCount = message.length;
    const charLimit = 500;
    const canSubmit = stars >= 1 && stars <= 5 && !loading && !done;

    const submit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setError(null);
        try {
            const r = await apiFetch(`${apiBase}/api/feedback/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    stars,
                    message: message.trim(),
                    walletAddress: walletAddress || null,
                    appVersion: APP_VERSION,
                }),
            });
            if (!r.ok) {
                // Try to surface the server's error message; fall back to status
                let serverMsg = `HTTP ${r.status}`;
                try {
                    const body = await r.json();
                    if (body && typeof body.error === 'string') serverMsg = body.error;
                } catch { /* keep status code as the message */ }
                throw new Error(serverMsg);
            }
            // Success — DB row durable; Telegram delivery is best-effort and not
            // gated here (per Scorer §3: DB canonical, Telegram notification only).
            setDone(true);
        } catch (err: any) {
            console.error('🚨 CF-FEEDBACK-V1: feedback submit failed', err);
            setError(err?.message || t('feedback.error.submit_failed'));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="pb-4">
                <div className="text-center py-12">
                    <span className="text-4xl">✨</span>
                    <h2 className="text-lg font-black mt-4">{t('feedback.success_title')}</h2>
                    <p className="text-[12px] mt-2" style={{ color: 'var(--text-secondary)' }}>
                        {t('feedback.success_body')}
                    </p>
                    <button
                        onClick={onClose}
                        className="btn-primary px-6 py-3 rounded-xl text-[13px] font-bold mt-6"
                    >
                        {t('common.done_btn')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pb-4">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-black tracking-tight">{t('feedback.title')}</h2>
                <button
                    onClick={onClose}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(148,163,184,0.08)', color: 'var(--text-secondary)', border: '1px solid rgba(148,163,184,0.18)' }}
                >
                    {t('common.cancel_btn')}
                </button>
            </div>

            <p className="text-[12px] mb-4" style={{ color: 'var(--text-secondary)' }}>
                {t('feedback.intro')}
            </p>

            {/* Star rating */}
            <div className="glass rounded-xl p-4 mb-4">
                <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--text-muted)' }}>
                    {t('feedback.rating_prompt')}
                </p>
                <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5].map(n => {
                        const active = n <= (hoverStars || stars);
                        return (
                            <button
                                key={n}
                                onClick={() => setStars(n)}
                                onMouseEnter={() => setHoverStars(n)}
                                onMouseLeave={() => setHoverStars(0)}
                                className="text-3xl transition-transform active:scale-90"
                                style={{ filter: active ? 'none' : 'grayscale(1) opacity(0.35)' }}
                                aria-label={n > 1 ? t('feedback.star_aria_plural', { n }) : t('feedback.star_aria_singular', { n })}
                            >
                                ⭐
                            </button>
                        );
                    })}
                </div>
                {stars > 0 && (
                    <p className="text-[10px] text-center mt-2" style={{ color: 'var(--text-muted)' }}>
                        {t('feedback.stars_count', { stars })}
                    </p>
                )}
            </div>

            {/* Message */}
            <div className="glass rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold tracking-wider uppercase" style={{ color: 'var(--text-muted)' }}>
                        {t('feedback.message_label')}
                    </p>
                    <span className="text-[10px] tabular-nums" style={{ color: charCount > charLimit ? 'var(--red)' : 'var(--text-muted)' }}>
                        {t('feedback.char_counter', { charCount, charLimit })}
                    </span>
                </div>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, charLimit))}
                    placeholder={t('feedback.message_placeholder')}
                    rows={5}
                    className="w-full bg-transparent text-[13px] outline-none resize-none"
                    style={{ color: 'var(--text-primary)' }}
                    maxLength={charLimit}
                />
            </div>

            {/* Safety reminder */}
            <p className="text-[10px] text-center mb-4 px-2" style={{ color: 'var(--text-muted)' }}>
                {t('feedback.safety_reminder')}
            </p>

            {/* Error */}
            {error && (
                <div className="rounded-lg px-3 py-2 mb-4" style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <p className="text-[11px] font-semibold" style={{ color: 'var(--red)' }}>
                        {error}
                    </p>
                </div>
            )}

            {/* Submit */}
            <button
                onClick={submit}
                disabled={!canSubmit}
                className="btn-primary w-full py-3.5 rounded-xl text-[14px] font-black tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? t('feedback.submit_btn_loading') : t('feedback.submit_btn')}
            </button>
        </div>
    );
}
